/**
 * The hatchery below deck (ADR 0033, lifted from brad-airship
 * `airship-hatchery.js`, "actor pass 3"): staggered planks over a recessed
 * floor, the walls from the room grid (post, panel and moulding; the north
 * wall is the ship's side with frames between the portlights and deck beams
 * on hanging knees, the east and west walls are bulkheads with stiffeners),
 * the doorway posts and headers at the grid's doors, the hatch log and the
 * linen shelf on the west wall, the heating main, and at each incubator the
 * turned legs, octagonal body, straw nest, brass hood and lamp, gauge and
 * card holder, with its egg. The hatch spot is a straw bed in a low kerb.
 * Every footprint is the room's (walls and doors from `@ab/scene`'s layout,
 * workstations from the scene record); the builder only draws.
 *
 * Changed from the prototype: the kit's materials and finish (cutaway and the
 * radiance volume); the egg is a dynamic mesh hidden at build, the world
 * shows it by its incubator's job (the prototype read the mock state here),
 * and its tag is `tagMesh` rather than `userData`. The random stream is the
 * kit's LCG, the prototype's sequence for the same seed.
 */
import * as THREE from 'three';
import type { RoomBuild } from './area.ts';
import { type ShipKit, type Vec3, at, tag, tagMesh } from './kit.ts';
import { addCompanionLadder, addPortlight, hullFrameStations, hullGlazing, portlightStations } from './room-kit.ts';
import type { DynamicMesh } from './types.ts';

export const buildHatchery = (kit: ShipKit, room: RoomBuild, seed = 537): { root: THREE.Group; dynamic: DynamicMesh[] } => {
  const m = kit.m;
  const trim = kit.get('trim');
  const timber = kit.get('timber');
  const cream = kit.get('cream');
  const warm = kit.get('warm');
  const soil = kit.get('soil');
  const red = kit.get('red');
  const root = new THREE.Group();
  root.name = `prefab:${room.id}:${room.prefab}:hatchery-art`;
  const dynamic: DynamicMesh[] = [];
  const rand = kit.random(seed);
  const material = (name: string, options: THREE.MeshStandardMaterialParameters): THREE.MeshStandardMaterial => {
    const have = m[name];
    if (have !== undefined) return have;
    const mat = kit.finish(new THREE.MeshStandardMaterial({ roughness: 0.92, ...options }));
    mat.name = `airship-${name}`;
    m[name] = mat;
    return mat;
  };
  const wood = material('hatcheryWood', { color: '#77654e', vertexColors: true });
  const copper = material('hatcheryCopper', { color: '#946b50', roughness: 0.68, metalness: 0.12, vertexColors: true });
  const lamp = material('hatcheryLamp', { color: '#ffe4a2', emissive: '#ffc65b', emissiveIntensity: 0.25 });
  const glazing = hullGlazing(kit);
  const add = (g: THREE.BufferGeometry, mat: THREE.MeshStandardMaterial, x = 0, y = 0, z = 0, cut = false, tint = 1): THREE.Mesh => {
    const count = g.getAttribute('position').count;
    tag(g, cut);
    // Every material batch must have identical attributes. Vertex tint supplies wood variation without adding
    // textures or a material per plank.
    if (mat.vertexColors) {
      const normal = g.getAttribute('normal');
      const colors = new Float32Array(count * 3);
      for (let i = 0; i < count; i += 1) {
        const ny = normal.getY(i);
        const nx = Math.abs(normal.getX(i));
        const facet = mat === copper ? (ny > 0.5 ? 0.96 : 0.6 + nx * 0.13) : ny > 0.5 ? 1 : 0.76;
        colors[i * 3] = tint * facet;
        colors[i * 3 + 1] = tint * facet;
        colors[i * 3 + 2] = tint * facet;
      }
      g.setAttribute('color', new THREE.Float32BufferAttribute(colors, 3));
    }
    const mesh = new THREE.Mesh(g, mat);
    mesh.position.set(x, y, z);
    root.add(mesh);
    return mesh;
  };
  const box = (mat: THREE.MeshStandardMaterial, x: number, y: number, z: number, w: number, h: number, d: number, cut = false, tint = 1): THREE.Mesh =>
    add(new THREE.BoxGeometry(w, h, d), mat, x, y, z, cut, tint);
  const cyl = (mat: THREE.MeshStandardMaterial, x: number, y: number, z: number, r: number, h: number, n = 10, rt = r, cut = false): THREE.Mesh =>
    add(new THREE.CylinderGeometry(rt, r, h, n), mat, x, y, z, cut);
  const ring = (mat: THREE.MeshStandardMaterial, x: number, y: number, z: number, r: number, t = 0.035, n = 16): THREE.Mesh => {
    const g = new THREE.TorusGeometry(r, t, 4, n);
    g.rotateX(Math.PI / 2);
    return add(g, mat, x, y, z);
  };
  const beam = (mat: THREE.MeshStandardMaterial, a: Vec3, b: Vec3, width: number, depth = width, cut = false): THREE.Mesh => {
    const va = new THREE.Vector3(...a);
    const vb = new THREE.Vector3(...b);
    const v = vb.clone().sub(va);
    const g = new THREE.BoxGeometry(width, v.length(), depth);
    g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.normalize()));
    const c = va.add(vb).multiplyScalar(0.5);
    return add(g, mat, c.x, c.y, c.z, cut);
  };
  const pipe = (a: Vec3, b: Vec3, r = 0.065): void => {
    const va = new THREE.Vector3(...a);
    const vb = new THREE.Vector3(...b);
    const v = vb.clone().sub(va);
    const g = new THREE.CylinderGeometry(r, r, v.length(), 8);
    g.applyQuaternion(new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, 1, 0), v.normalize()));
    const c = va.add(vb).multiplyScalar(0.5);
    add(g, copper, c.x, c.y, c.z, true);
  };

  // Individual staggered planks over a continuous recessed structural floor. Their tops are exactly at the
  // walking surface; no overlaid floor plane.
  for (const s of room.localSurfaces) {
    box(trim, (s.x0 + s.x1) / 2, s.y - 0.2, (s.z0 + s.z1) / 2, s.x1 - s.x0, 0.28, s.z1 - s.z0);
    const rows = Math.ceil((s.z1 - s.z0) / 0.48);
    const depth = (s.z1 - s.z0) / rows;
    for (let row = 0; row < rows; row += 1) {
      const z = s.z0 + (row + 0.5) * depth;
      const cuts = [s.x0];
      for (let x = s.x0 + 1.5 + (row % 3) * 0.8; x < s.x1; x += 3.4) cuts.push(x);
      cuts.push(s.x1);
      for (let p = 0; p < cuts.length - 1; p += 1) {
        const a = at(cuts, p);
        const b = at(cuts, p + 1);
        const length = b - a;
        box(wood, (a + b) / 2, s.y - 0.035, z, length - 0.018, 0.07, depth - 0.018, false, 0.7 + rand() * 0.42);
        if (length > 0.5) {
          box(timber, a + length * 0.5, s.y + 0.004, z - depth * 0.23, length * 0.62, 0.008, 0.014);
          for (const x of row % 2 === 1 ? [a + 0.12] : []) box(trim, x, s.y + 0.005, z + 0.07, 0.033, 0.01, 0.033);
        }
      }
    }
    // A perimeter binding and the exposed cutaway floor fascia carry joinery.
    for (const z of [s.z0 + 0.1, s.z1 - 0.1]) {
      box(wood, (s.x0 + s.x1) / 2, s.y + 0.022, z, s.x1 - s.x0, 0.044, 0.16);
      box(copper, (s.x0 + s.x1) / 2, s.y - 0.21, z, s.x1 - s.x0, 0.045, 0.18);
    }
    for (const x of [s.x0 + 0.1, s.x1 - 0.1]) {
      box(wood, x, s.y + 0.023, (s.z0 + s.z1) / 2, 0.16, 0.046, s.z1 - s.z0);
      box(copper, x, s.y - 0.21, (s.z0 + s.z1) / 2, 0.18, 0.045, s.z1 - s.z0);
    }
  }

  // Each wall segment from the grid with a post, panel and moulding hierarchy.
  for (const c of room.localColliders.filter((w) => w.kind === 'wall')) {
    const horizontal = c.side === 'north' || c.side === 'south';
    const start = horizontal ? c.x0 : c.z0;
    const end = horizontal ? c.x1 : c.z1;
    const fixed = horizontal ? (c.z0 + c.z1) / 2 : (c.x0 + c.x1) / 2;
    const front = c.side === 'south';
    const h = front ? 0.38 : c.height;
    const pos = (u: number, y: number, inset = 0): Vec3 => (horizontal ? [u, y, fixed + inset] : [fixed + inset, y, u]);
    const wallBox = (mat: THREE.MeshStandardMaterial, u: number, y: number, w: number, hh: number, d: number, inset = 0): THREE.Mesh => {
      const [x, yy, z] = pos(u, y, inset);
      return box(mat, x, yy, z, horizontal ? w : d, hh, horizontal ? d : w, true);
    };
    wallBox(trim, (start + end) / 2, h / 2, end - start, h, 0.11);
    for (const y of [0.12, h - 0.06]) wallBox(wood, (start + end) / 2, y, end - start, 0.13, 0.25);
    // The north wall is the ship's side: frames stand between the portlights. The east and west walls are
    // transverse bulkheads with stiffening posts.
    const hull = c.side === 'north';
    let bayEdges = [start];
    if (hull) bayEdges = hullFrameStations(start, end);
    else {
      const divisions = Math.ceil((end - start) / 2.1);
      const pattern = [1.4, 0.82, 1.16, 0.7, 1.26, 0.9];
      const weights = Array.from({ length: divisions }, (_, j) => at(pattern, (j + (c.side === 'west' ? 2 : 0)) % 6));
      const sum = weights.reduce((a, b) => a + b, 0);
      for (const weight of weights) bayEdges.push(at(bayEdges, bayEdges.length - 1) + ((end - start) * weight) / sum);
    }
    for (const u of bayEdges) {
      wallBox(wood, u, h / 2, 0.14, h + 0.06, 0.26);
      for (const y of [0.19, h - 0.19]) wallBox(copper, u, y, 0.18, 0.052, 0.285);
    }
    if (!front) {
      const inward = c.side === 'north' || c.side === 'west' ? 1 : -1;
      // Stringer and capping piece.
      wallBox(wood, (start + end) / 2, 0.72, end - start, 0.15, 0.25);
      wallBox(trim, (start + end) / 2, h + 0.06, end - start + 0.03, 0.1, 0.29);
      if (hull) {
        // Inner planking runs fore and aft: strake seams, not wainscot.
        for (const y of [0.42, 1.02, 1.98, 2.3]) wallBox(timber, (start + end) / 2, y, end - start, 0.018, 0.125);
        for (const u of portlightStations(start, end)) {
          const [x, y, z] = pos(u, 1.5, inward * 0.06);
          addPortlight(root, kit, glazing, x, y, z);
        }
        // Deck beams bear on the clamp at every frame, with a hanging knee. The deck above is cut away, so each
        // beam shows only its end.
        for (const u of bayEdges.slice(1, -1)) {
          wallBox(wood, u, h + 0.24, 0.2, 0.24, 1.7, inward * 0.87);
          beam(wood, pos(u, h - 0.62, inward * 0.15), pos(u, h + 0.12, inward * 0.62), 0.12, 0.1, true);
        }
      } else {
        for (let u = start + 0.45; u < end - 0.45; u += 1.5) {
          wallBox(timber, u, h - 0.38, 0.5, 0.055, 0.22);
          beam(wood, pos(u - 0.25, h - 0.13, inward * 0.125), pos(u + 0.25, h - 0.64, inward * 0.125), 0.06, 0.055, true);
        }
      }
      if (c.side === 'west') {
        // Hatch log: a slate with one card per incubator, beside incubator 1.
        const u = -3.2;
        const face = inward * 0.075;
        wallBox(trim, u, 1.5, 1.0, 0.72, 0.03, face);
        for (const du of [-0.5, 0.5]) wallBox(wood, u + du, 1.5, 0.05, 0.8, 0.05, face + inward * 0.01);
        for (const yy of [1.13, 1.87]) wallBox(wood, u, yy, 1.05, 0.05, 0.05, face + inward * 0.01);
        for (let k = 0; k < 3; k += 1) wallBox(cream, u - 0.3 + k * 0.3, 1.55, 0.2, 0.27, 0.01, face + inward * 0.02);
        // Peg rail for egg tongs and the candling lamp.
        const t = 0.8;
        wallBox(wood, t, 1.72, 0.9, 0.07, 0.06, inward * 0.09);
        wallBox(trim, t - 0.2, 1.5, 0.05, 0.42, 0.04, inward * 0.12);
        wallBox(copper, t + 0.22, 1.56, 0.14, 0.2, 0.12, inward * 0.14);
        wallBox(warm, t + 0.22, 1.56, 0.1, 0.12, 0.125, inward * 0.14);
        // Linen for lining the nests, on a bracketed shelf above head height.
        const sh = start + (end - start) * 0.27;
        const y = h - 0.19;
        wallBox(wood, sh, y, 1.3, 0.1, 0.42, inward * 0.18);
        wallBox(cream, sh - 0.16, y + 0.105, 0.75, 0.11, 0.31, inward * 0.22);
        wallBox(cream, sh - 0.23, y + 0.19, 0.55, 0.06, 0.29, inward * 0.22);
        wallBox(warm, sh + 0.4, y + 0.14, 0.27, 0.17, 0.28, inward * 0.22);
        beam(copper, pos(sh - 0.5, y - 0.34, inward * 0.12), pos(sh - 0.5, y - 0.05, inward * 0.36), 0.045, 0.045, true);
        beam(copper, pos(sh + 0.5, y - 0.34, inward * 0.12), pos(sh + 0.5, y - 0.05, inward * 0.36), 0.045, 0.045, true);
      }
      // Heating main: enters through the aft bulkhead from the engine room and runs along the walls; each
      // incubator tees off it (below).
      if (end - start > 0.65) {
        pipe(pos(start + 0.17, h - 0.55, inward * 0.16), pos(end - 0.17, h - 0.55, inward * 0.16));
        for (let u = start + 0.4; u < end - 0.25; u += 1.1) wallBox(copper, u, h - 0.55, 0.1, 0.18, 0.36);
      }
    }
  }

  // Doorway posts, split walls and headers at the grid's door centres.
  for (const d of room.localDoors) {
    const horizontal = d.side === 'north' || d.side === 'south';
    const front = d.side === 'south';
    const along = horizontal ? d.x : d.z;
    const fixed = horizontal ? d.z : d.x;
    const doorAt = (u: number, y: number): Vec3 => (horizontal ? [u, y, fixed] : [fixed, y, u]);
    for (const sign of [-1, 1]) {
      const [x, y, z] = doorAt(along + (sign * d.width) / 2, front ? 0.25 : 1.4);
      box(wood, x, y, z, 0.23, front ? 0.5 : 2.8, 0.23, true);
      box(copper, x, 0.12, z, 0.26, 0.08, 0.26, true);
    }
    if (!front) {
      const [x, y, z] = doorAt(along, 2.8);
      box(wood, x, y, z, horizontal ? d.width + 0.3 : 0.25, 0.22, horizontal ? 0.25 : d.width + 0.3, true);
      const [xx, yy, zz] = doorAt(along, 2.96);
      box(copper, xx, yy, zz, horizontal ? 0.6 : 0.29, 0.09, horizontal ? 0.29 : 0.6, true);
    }
  }

  let incubatorIndex = 0;
  for (const fixture of room.localInteractions) {
    const { x, z } = fixture.pos;
    if (fixture.kind === 'incubator') {
      const r = Math.min(fixture.w, fixture.d) * 0.45;
      const top = fixture.h;
      const variant = incubatorIndex;
      incubatorIndex += 1;
      // Short turned legs, octagonal body, copper seam and a nested cradle.
      for (const dx of [-r * 0.7, r * 0.7]) {
        for (const dz of [-r * 0.7, r * 0.7]) {
          cyl(wood, x + dx, 0.22, z + dz, 0.085, 0.44, 6, 0.065);
          cyl(copper, x + dx, 0.055, z + dz, 0.11, 0.11, 6);
        }
      }
      cyl(wood, x, 0.46, z, r, 0.48, 8, r * 0.92);
      cyl(copper, x, 0.26, z, r + 0.015, 0.07, 8);
      cyl(copper, x, 0.69, z, r + 0.025, 0.055, 8);
      cyl(wood, x, 0.85, z, r * 0.9, 0.3, 8, r * 0.82);
      cyl(soil, x, top - 0.1, z, r * 0.82, 0.19, 12, r * 0.93);
      ring(copper, x, top, z, r * 0.89, 0.055, 16);
      ring(timber, x, top - 0.12, z, r * 0.95, 0.04, 16);
      for (let n = 0; n < 16; n += 1) {
        const a = rand() * Math.PI * 2;
        const rr = r * (0.34 + rand() * 0.35);
        const len = 0.14 + rand() * 0.22;
        const straw = box(n % 3 !== 0 ? warm : cream, x + Math.cos(a) * rr, top + 0.017 + n * 0.0003, z + Math.sin(a) * rr, len, 0.013, 0.019);
        straw.rotation.y = rand() * Math.PI;
      }
      // Brass frame, hood and broad glowing shade stay inside the fixture's footprint.
      for (const sign of [-1, 1]) {
        cyl(copper, x + sign * r * 0.78, top + 0.47, z - r * 0.5, 0.033, 0.92, 6);
        pipe([x + sign * r * 0.78, top + 0.93, z - r * 0.5], [x, top + 1.07, z - r * 0.5], 0.033);
      }
      cyl(copper, x, top + 1.0, z - r * 0.5, r * 0.38, 0.2, 10, r * 0.18);
      // The exposed warm core stays below the hood lip and behind the egg's crown.
      cyl(lamp, x, top + 0.75, z - r * 0.45, r * 0.22, 0.12, 10);
      // Nest thermometer and the brass card holder for this egg's record.
      const gauge = cyl(cream, x + r * 0.44, 0.87, z + r * 0.72, 0.11, 0.06, 12);
      gauge.rotation.x = Math.PI / 2;
      box(trim, x + r * 0.44, 0.87, z + r * 0.76, 0.016, 0.12, 0.01);
      box(copper, x - r * 0.32, 0.55, z + r * 0.89, 0.27, 0.17, 0.045);
      for (let n = 0; n < 3; n += 1) box(trim, x - r * 0.41 + n * 0.08, 0.55, z + r * 0.919, 0.025, 0.065, 0.012);
      // The lamp burner is fed from the heating main on the hull wall, with its own shut-off handwheel beside the tee.
      const mainY = 2.25;
      const mainZ = -room.depth / 2 + 0.26;
      pipe([x, mainY, mainZ], [x, mainY, z - r * 0.5], 0.04);
      pipe([x, mainY, z - r * 0.5], [x, top + 1.08, z - r * 0.5], 0.04);
      ring(red, x, mainY + 0.13, mainZ + 0.55, 0.1, 0.025, 10);
      cyl(copper, x, mainY + 0.06, mainZ + 0.55, 0.02, 0.12, 6);
      // The egg: one mutable mesh per incubator, its look driven by the incubator's job.
      const eggMat = kit.own(kit.finish(kit.get('marker').clone()));
      eggMat.vertexColors = true;
      eggMat.flatShading = true;
      eggMat.roughness = 0.86;
      const eggG = new THREE.SphereGeometry(0.3, 12, 8);
      const ep = eggG.getAttribute('position');
      for (let n = 0; n < ep.count; n += 1) {
        const yy = ep.getY(n);
        ep.setXYZ(n, ep.getX(n) * (1 - yy * 0.6), yy * 1.35, ep.getZ(n) * (1 - yy * 0.6));
      }
      eggG.computeVertexNormals();
      const egg = add(eggG.toNonIndexed(), eggMat, x, top + 0.37, z);
      eggG.dispose();
      // Shell facets keep dimensional value under the warm irradiance; these vertex colours survive the material
      // colour the world sets by the job's state.
      const shell = egg.geometry.getAttribute('position');
      const shellColors = egg.geometry.getAttribute('color');
      for (let q = 0; q < shell.count; q += 3) {
        const cx = (shell.getX(q) + shell.getX(q + 1) + shell.getX(q + 2)) / 3;
        const cy = (shell.getY(q) + shell.getY(q + 1) + shell.getY(q + 2)) / 3;
        const cz = (shell.getZ(q) + shell.getZ(q + 1) + shell.getZ(q + 2)) / 3;
        const spot = Math.sin(cx * 47 + cz * 23 + variant * 2) > 0.82 && cy < 0.24 ? 0.73 : 1;
        const value = (0.67 + cy * 0.43 - cx * 0.21 + cz * 0.07) * spot;
        for (let j = 0; j < 3; j += 1) shellColors.setXYZ(q + j, value, value * 0.98, value * 0.88);
      }
      shellColors.needsUpdate = true;
      egg.name = `incubator:${fixture.id}`;
      tagMesh(egg, { dynamic: true });
      egg.visible = false;
      dynamic.push({ id: fixture.id, mesh: egg, kind: 'incubator' });
    } else if (fixture.kind === 'hatch-spot') {
      // The hatch spot stays non-solid: a straw bed in a low kerb where the hatchling is set down, with a shallow
      // water dish.
      const w = fixture.w;
      const dd = fixture.d;
      box(warm, x, 0.03, z, w - 0.12, 0.06, dd - 0.12);
      for (const dz of [-dd / 2 + 0.05, dd / 2 - 0.05]) box(wood, x, 0.07, z + dz, w, 0.14, 0.1, false, 0.8);
      for (const dx of [-w / 2 + 0.05, w / 2 - 0.05]) box(wood, x + dx, 0.07, z, 0.1, 0.14, dd - 0.2, false, 0.8);
      for (let n = 0; n < 12; n += 1) {
        const straw = box(n % 3 !== 0 ? warm : cream, x + (rand() - 0.5) * (w - 0.4), 0.066, z + (rand() - 0.5) * (dd - 0.4), 0.16 + rand() * 0.12, 0.012, 0.02);
        straw.rotation.y = rand() * Math.PI;
      }
      cyl(copper, x + w * 0.3, 0.07, z - dd * 0.3, 0.14, 0.05, 10, 0.16);
    }
  }
  // The exit portal sits 1.3 m inside the south doorway; a companionway ladder climbs from it to the deck hatch overhead.
  for (const d of room.localDoors.filter((q) => q.side === 'south')) addCompanionLadder(root, kit, d.x, d.z - 1.3);
  if (room.level > 1) {
    // The second-level facility adds a reinforced copper crown and status plaques.
    for (const c of room.localColliders.filter((w) => w.kind === 'wall' && w.side === 'north')) {
      box(copper, (c.x0 + c.x1) / 2, c.height + 0.18, (c.z0 + c.z1) / 2, c.x1 - c.x0, 0.08, 0.29, true);
      box(kit.get('blue'), (c.x0 + c.x1) / 2, c.height - 0.45, (c.z0 + c.z1) / 2 + 0.17, 0.72, 0.35, 0.055, true);
      for (const dx of [-0.12, 0.12]) box(warm, (c.x0 + c.x1) / 2 + dx, c.height - 0.45, (c.z0 + c.z1) / 2 + 0.205, 0.065, 0.22, 0.025, true);
    }
  }
  return { root, dynamic };
};
